import React, { useState } from 'react';
import { Plus, Upload, X } from 'lucide-react';
import { Location, LocationCategory } from '../types';
import { Map } from './Map';
import { uploadImages, uploadImagesAdmin } from '../utils/images';
import { validateImageFile } from '../utils/images';
import toast from 'react-hot-toast';

interface EditLocationProps {
  location: Location;
  onSave: (location: Location) => void;
  onClose: () => void;
  isAdmin?: boolean;
}

export const EditLocation: React.FC<EditLocationProps> = ({ 
  location, 
  onSave, 
  onClose, 
  isAdmin = false 
}) => {
  const [formData, setFormData] = useState({
    ...location
  });

  const [tagInput, setTagInput] = useState('');
  const [newImageFiles, setNewImageFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories: { value: LocationCategory; label: string }[] = [
    { value: 'eco-trail', label: 'Экасцежка' },
    { value: 'camping', label: 'Кемпінг' },
    { value: 'stargazing', label: 'Назіранне за зоркамі' },
    { value: 'picnic', label: 'Пікнік' },
    { value: 'eco-hotel', label: 'Экаатэль' },
    { value: 'estate', label: 'Сядзіба' },
    { value: 'zoo', label: 'Звярынцы' },
    { value: 'Historical places', label: 'Гістарычныя месцы' },
    { value: 'other', label: 'Іншае' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let updatedImages = [...formData.images];

      // Upload new images if any
      if (newImageFiles.length > 0) {
        console.log('Uploading new images to Supabase Storage...');
        
        // Use admin upload function if in admin mode
        const uploadedImageUrls = isAdmin 
          ? await uploadImagesAdmin(newImageFiles)
          : await uploadImages(newImageFiles);
          
        console.log('New images uploaded successfully:', uploadedImageUrls);
        updatedImages = [...updatedImages, ...uploadedImageUrls];
      }

      // Save location with updated images (all URLs)
      onSave({
        ...formData,
        images: updatedImages
      });
    } catch (error) {
      console.error('Error updating location:', error);
      toast.error(error instanceof Error ? error.message : 'Не атрымалася абнавіць лакацыю');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTagAdd = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleTagRemove = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImageAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    for (const file of files) {
      try {
        validateImageFile(file);
        setNewImageFiles(prev => [...prev, file]);
        toast.success('Фота дададзена для загрузкі');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Памылка пры даданні файла');
      }
    }
  };

  const handleImageRemove = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleNewImageRemove = (index: number) => {
    setNewImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">
              {isAdmin ? 'Рэдагаваць лакацыю (Адмін)' : 'Рэдагаваць лакацыю'}
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Назва
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    value={formData.name}
                    onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Кароткае апісанне
                  </label>
                  <textarea
                    required
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    value={formData.shortDescription}
                    onChange={e => setFormData(prev => ({ ...prev, shortDescription: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Поўнае апісанне
                  </label>
                  <textarea
                    required
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    value={formData.description}
                    onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Катэгорыя
                  </label>
                  <select
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    value={formData.category}
                    onChange={e => setFormData(prev => ({ ...prev, category: e.target.value as LocationCategory }))}
                  >
                    {categories.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Хэштэгі
                  </label>
                  <div className="flex gap-2 mb-2 flex-wrap">
                    {formData.tags.map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-sm"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleTagRemove(tag)}
                          className="ml-1 text-gray-500 hover:text-gray-700"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), handleTagAdd())}
                      placeholder="Дадайце хэштэг..."
                    />
                    <button
                      type="button"
                      onClick={handleTagAdd}
                      className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Фотаздымкі
                  </label>
                  
                  {/* Existing images */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={image}
                          alt={`Фота ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => handleImageRemove(index)}
                          className="absolute top-1 right-1 bg-white rounded-full p-1 shadow-md"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* New images to upload */}
                  {newImageFiles.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">Новыя фота для загрузкі:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {newImageFiles.map((file, index) => (
                          <div key={index} className="relative">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`Новая фота ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg border-2 border-blue-300"
                            />
                            <button
                              type="button"
                              onClick={() => handleNewImageRemove(index)}
                              className="absolute top-1 right-1 bg-white rounded-full p-1 shadow-md"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add new images */}
                  {(formData.images.length + newImageFiles.length) < 5 && (
                    <label className="border-2 border-dashed border-gray-300 rounded-lg h-32 flex items-center justify-center cursor-pointer hover:border-primary">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageAdd}
                        multiple
                      />
                      <Upload className="text-gray-400" size={24} />
                    </label>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Выберыце месца на карце
                </label>
                <Map
                  center={[formData.coordinates.lat, formData.coordinates.lng]}
                  onClick={(lat, lng) => setFormData(prev => ({
                    ...prev,
                    coordinates: { lat, lng }
                  }))}
                  withSearch={true}
                />
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isSubmitting}
              >
                Адмяніць
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Захаванне...' : 'Захаваць'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};